import { Center, Loader, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { signInAnonymously } from 'firebase/auth';
import type { ReactNode } from 'react';
import { getFirebaseAuth } from '../firebase/client';
import { useAuth } from './AuthProvider';

/**
 * Auth gate for the games area. Unlike `RequireAuth` (which walls the rest of
 * the platform behind a real account), games are open to anyone with a room
 * link — so a friend at a party can scan a QR and join from their phone.
 *
 * If there's no Firebase session yet, we sign the visitor in *anonymously*.
 * That gives them a `request.auth` token (so the Firestore rules, which now
 * require auth, let them read/write room docs) without needing a minted
 * account. A real (non-anonymous) user passes straight through and keeps
 * their identity.
 *
 * Anonymous sessions are confined to `/games/*`: `RequireAuth` rejects
 * anonymous users everywhere else, so a guest can't wander into the rest of
 * the platform.
 *
 * NOTE: the Firebase project must have the Anonymous sign-in provider enabled
 * (Authentication → Sign-in method). If it's disabled, `signInAnonymously`
 * throws `auth/operation-not-allowed` and we surface a clear message.
 */
export function GameAuthGate({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const attempted = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || user || attempted.current) return;
    attempted.current = true;
    signInAnonymously(getFirebaseAuth()).catch((err: { code?: string }) => {
      setError(
        err.code === 'auth/operation-not-allowed'
          ? 'Guest sign-in is disabled for this site. Enable Anonymous auth in the Firebase console.'
          : 'Could not start a guest session. Please try again.',
      );
    });
  }, [loading, user]);

  if (error) {
    return (
      <Center mih="100vh">
        <Stack align="center" gap="xs">
          <Text c="red" fw={600}>
            Sign-in problem
          </Text>
          <Text c="dimmed" size="sm" ta="center" maw={360}>
            {error}
          </Text>
        </Stack>
      </Center>
    );
  }

  if (loading || !user) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  return <>{children}</>;
}
